import React, { useState, useEffect } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { Add as AddIcon, Visibility as ViewIcon, PlayArrow as ProcessIcon } from '@mui/icons-material';

import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { fetchEntities, createEntity, clearError, processEntity } from '../store/slices/entitySlice';
import { showSuccess, showError, showInfo } from '../store/slices/toastSlice';
import { Entity, EntityStatus } from '../types';
import { config } from '../config';

interface CreateEntityForm {
  name: string;
  description: string;
  data: string;
}

interface ValidationErrors {
  name?: string;
  description?: string;
  data?: string;
}

const Entities: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { entities, isLoading, error } = useAppSelector((state) => state.entities);

  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Keep hook ordering consistent
  usePageMeta('Knowledge Bases – AskNow Admin', 'Create, process, and manage knowledge bases for your AskNow AI chatbot.');
  const [createForm, setCreateForm] = useState<CreateEntityForm>({
    name: '',
    description: '',
    data: '',
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isCreating, setIsCreating] = useState(false);
  const [processingEntities, setProcessingEntities] = useState<Set<string>>(new Set());
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  useEffect(() => {
    dispatch(fetchEntities());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(showError({
        title: 'Error',
        message: error,
        duration: 5000,
      }));
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleProcessEntity = async (entityId: string) => {
    setProcessingEntities(prev => new Set(prev).add(entityId));
    try {
      await dispatch(processEntity(entityId)).unwrap();
      dispatch(showSuccess({
        title: 'Success',
        message: 'Knowledge base processed successfully!',
        duration: 3000,
      }));
      // Refresh the entities list to update the status
      dispatch(fetchEntities());
    } catch (error: any) {
      dispatch(showError({
        title: 'Processing Failed',
        message: error.message || 'Failed to process knowledge base',
        duration: 5000,
      }));
    } finally {
      setProcessingEntities(prev => {
        const newSet = new Set(prev);
        newSet.delete(entityId);
        return newSet;
      });
    }
  };

  const handleBulkProcess = async () => {
    if (selectedEntities.length === 0) return;
    
    setIsBulkProcessing(true);
    const unprocessedEntities = selectedEntities.filter(id => {
      const entity = entities.find(e => e.id === id || e.entity_id === id);
      return entity && !entity.is_processed;
    });
    
    if (unprocessedEntities.length === 0) {
      dispatch(showInfo({
        title: 'No Processing Needed',
        message: 'All selected knowledge bases are already processed.',
        duration: 3000,
      }));
      setIsBulkProcessing(false);
      return;
    }
    
    try {
      for (const entityId of unprocessedEntities) {
        await dispatch(processEntity(entityId)).unwrap();
      }
      
      dispatch(showSuccess({
        title: 'Bulk Processing Complete',
        message: `Successfully processed ${unprocessedEntities.length} knowledge base(s)!`,
        duration: 4000,
      }));
      
      setSelectedEntities([]);
      // Refresh the entities list to update the status
      dispatch(fetchEntities());
    } catch (error: any) {
      dispatch(showError({
        title: 'Bulk Processing Failed',
        message: error.message || 'Failed to process some knowledge bases',
        duration: 5000,
      }));
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    // Name validation
    if (!createForm.name.trim()) {
      errors.name = 'Entity name is required';
    } else if (createForm.name.length < config.validation.entityName.minLength) {
      errors.name = `Name must be at least ${config.validation.entityName.minLength} characters`;
    } else if (createForm.name.length > config.validation.entityName.maxLength) {
      errors.name = `Name must be less than ${config.validation.entityName.maxLength} characters`;
    }

    // Description validation
    if (createForm.description && createForm.description.length > config.validation.entityDescription.maxLength) {
      errors.description = `Description must be less than ${config.validation.entityDescription.maxLength} characters`;
    }

    // Data validation
    if (!createForm.data.trim()) {
      errors.data = 'Knowledge base data is required';
    } else if (createForm.data.length < 10) {
      errors.data = 'Data must be at least 10 characters long';
    } else if (createForm.data.length > 10000) {
      errors.data = 'Data must be less than 10,000 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateEntity = async () => {
    if (!validateForm()) {
      return;
    }

    setIsCreating(true);
    try {
      const createdEntity = await dispatch(createEntity({
        name: createForm.name.trim(),
        description: createForm.description.trim() || undefined,
        data: createForm.data.trim(),
      })).unwrap();

      dispatch(showSuccess({
        title: 'Success',
        message: 'Knowledge base created successfully',
        duration: 3000,
      }));

      // Automatically process the entity after creation
      try {
        await dispatch(processEntity(createdEntity.id || createdEntity.entity_id)).unwrap();
        dispatch(showSuccess({
          title: 'Processing Complete',
          message: 'Knowledge base has been automatically processed and is ready to use!',
          duration: 4000,
        }));
      } catch (processError: any) {
        dispatch(showInfo({
          title: 'Processing Note',
          message: 'Knowledge base created successfully, but automatic processing failed. You can manually process it later.',
          duration: 5000,
        }));
      }
      
      setCreateDialogOpen(false);
      setCreateForm({ name: '', description: '', data: '' });
      setValidationErrors({});
    } catch (error: any) {
      dispatch(showError({
        title: 'Creation Failed',
        message: error.message || 'Failed to create knowledge base',
        duration: 5000,
      }));
    } finally {
      setIsCreating(false);
    }
  };

  const getStatusColor = (status: EntityStatus) => {
    switch (status) {
      case EntityStatus.ACTIVE:
        return 'success';
      case EntityStatus.PROCESSING:
        return 'warning';
      case EntityStatus.INACTIVE:
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredEntities = entities.filter(entity =>
    (entity.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (entity.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (entity.data?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 300,
    },
    {
      field: 'data',
      headerName: 'Content Preview',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 200,
          }}
        >
          {params.value ? `${params.value.substring(0, 100)}${params.value.length > 100 ? '...' : ''}` : 'No content'}
        </Typography>
      ),
    },
    {
      field: 'processing_status',
      headerName: 'Processing Status',
      width: 150,
      renderCell: (params) => {
        const status = params.value;
        let color: 'success' | 'warning' | 'error' | 'default' = 'default';
        let label = status || 'Not Started';
        
        if (status === 'completed') {
          color = 'success';
          label = 'Completed';
        } else if (status === 'processing') {
          color = 'warning';
          label = 'Processing';
        } else if (status === 'failed') {
          color = 'error';
          label = 'Failed';
        } else if (status === 'pending') {
          color = 'warning';
          label = 'Pending';
        }
        
        return (
          <Chip
            label={label}
            color={color}
            size="small"
          />
        );
      },
    },
    {
      field: 'processed_data',
      headerName: 'AI Processed',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'warning'}
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const entityId = params.row.entity_id || params.row.id;
        const isProcessing = processingEntities.has(entityId);
        const isAlreadyProcessed = params.row.processing_status === 'completed';
        
        return (
          <Box display="flex" gap={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(`/admin/entities/${entityId}`)}
              startIcon={<ViewIcon />}
            >
              View
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => handleProcessEntity(entityId)}
              startIcon={<ProcessIcon />}
              disabled={isProcessing || isAlreadyProcessed}
            >
              {isProcessing ? 'Processing...' : isAlreadyProcessed ? 'Processed' : 'Process'}
            </Button>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Knowledge Bases
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Knowledge Base
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search knowledge bases..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
          />
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedEntities.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="subtitle1">
                {selectedEntities.length} knowledge base(s) selected
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ProcessIcon />}
                onClick={handleBulkProcess}
                disabled={isBulkProcessing}
              >
                {isBulkProcessing ? 'Processing...' : 'Process Selected'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setSelectedEntities([])}
              >
                Clear Selection
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataGrid
            rows={filteredEntities}
            columns={columns}
            loading={isLoading}
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            getRowId={(row) => row.id || row.entity_id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            checkboxSelection
            onRowSelectionModelChange={(newSelection) => {
              setSelectedEntities(newSelection as string[]);
            }}
            rowSelectionModel={selectedEntities}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Create Entity Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Knowledge Base</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Note:</strong> Knowledge bases are automatically processed after creation to make them ready for AI interactions.
              </Typography>
            </Alert>
          </Box>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Knowledge Base Name"
                value={createForm.name}
                onChange={(e) => {
                  setCreateForm({ ...createForm, name: e.target.value });
                  if (validationErrors.name) {
                    setValidationErrors({ ...validationErrors, name: undefined });
                  }
                }}
                error={!!validationErrors.name}
                helperText={validationErrors.name || 'Enter a descriptive name for your knowledge base'}
                required
                placeholder="e.g., Company Policies, Product Documentation"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={createForm.description}
                onChange={(e) => {
                  setCreateForm({ ...createForm, description: e.target.value });
                  if (validationErrors.description) {
                    setValidationErrors({ ...validationErrors, description: undefined });
                  }
                }}
                error={!!validationErrors.description}
                helperText={validationErrors.description || 'Optional description of what this knowledge base contains'}
                multiline
                rows={2}
                placeholder="e.g., Internal company policies and procedures for employees"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Knowledge Base Data"
                value={createForm.data}
                onChange={(e) => {
                  setCreateForm({ ...createForm, data: e.target.value });
                  if (validationErrors.data) {
                    setValidationErrors({ ...validationErrors, data: undefined });
                  }
                }}
                error={!!validationErrors.data}
                helperText={validationErrors.data || 'Enter the content/data for your knowledge base. This will be processed by AI to create intelligent responses.'}
                multiline
                rows={8}
                required
                placeholder="Enter your knowledge base content here. This can include policies, procedures, FAQs, product information, or any other content you want the AI to understand and respond to..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateEntity} 
            variant="contained"
            disabled={isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Knowledge Base'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Entities;
